import * as THREE from 'three'
import RAPIER from '@dimforge/rapier3d-compat'
import { times } from './utils/lodash'
import { CoordinateTuple, Model } from '@webgamekit/animation'
import { GeneratedInstanceConfig, InstanceConfig, PhysicOptions } from './types'
import { SCENE_DEFAULTS } from './defaults'
import { textureLoader } from './loaders'

/**
 * Initialize typical configuration for ThreeJS and Rapier for a given canvas.
 * @param canvas
 * @param options
 * @returns
 */
type SceneOptions = {
  camera?: { position?: CoordinateTuple; distance?: number }
  scene?: { background?: number }
}

/**
 * Creates a renderer, scene, perspective camera, clock and physics world for a canvas, initialising
 * Rapier first.
 * @param canvas The canvas to render into.
 * @param options The camera's position and field of view, and the background colour.
 * @returns The renderer, scene, camera, clock and world.
 */
export const getScene = async (
  canvas: HTMLCanvasElement,
  options: SceneOptions = {
    camera: { position: SCENE_DEFAULTS.camera.position, distance: SCENE_DEFAULTS.camera.distance },
    scene: { background: SCENE_DEFAULTS.scene.background }
  }
) => {
  await RAPIER.init()
  const gravity = { x: 0.0, y: -9.81, z: 0.0 }
  const world = new RAPIER.World(gravity)
  const renderer = getRenderer(canvas)
  const scene = new THREE.Scene()
  const clock = new THREE.Clock()
  scene.background = new THREE.Color(options.scene?.background || SCENE_DEFAULTS.scene.background)

  const cameraConfig = options.camera ?? {
    position: SCENE_DEFAULTS.camera.position,
    distance: SCENE_DEFAULTS.camera.distance
  }
  const camera = new THREE.PerspectiveCamera(
    cameraConfig.distance,
    canvas.clientWidth / canvas.clientHeight,
    0.1,
    1000
  )
  camera.position.set(...(cameraConfig.position as CoordinateTuple))

  return { renderer, scene, camera, clock, world }
}

/**
 * Create and return ground with physic, helper and texture
 * @param scene The scene to add the ground to.
 * @param world The physics world to add its body to.
 * @returns The ground mesh, its fixed body and collider, and the helper when asked for.
 */
export const getGround = (
  scene: THREE.Scene,
  world: RAPIER.World,
  {
    size = SCENE_DEFAULTS.ground.size,
    position = SCENE_DEFAULTS.ground.position,
    helpers,
    color = SCENE_DEFAULTS.ground.color,
    texture,
    textureRepeat = SCENE_DEFAULTS.ground.textureRepeat,
    textureOffset = SCENE_DEFAULTS.ground.textureOffset,
    restitution = 0
  }: {
    size?: CoordinateTuple | number
    position?: CoordinateTuple
    helpers?: boolean
    color?: number
    texture?: string
    textureRepeat?: [number, number]
    textureOffset?: [number, number]
    restitution?: number
  }
) => {
  const defaultProps = { color }
  const groundSizes: CoordinateTuple = Array.isArray(size) ? size : [size, 0.01, size]
  const groundCenterY = position[1] - (groundSizes[1] || 0.01) / 2
  const groundPosition: CoordinateTuple = [position[0], groundCenterY, position[2]]
  const geometry = new THREE.BoxGeometry(...groundSizes)
  const material = new THREE.MeshStandardMaterial({
    ...defaultProps,
    ...(texture ? { map: getTextures(texture, textureRepeat, textureOffset) } : {})
  })

  const mesh = new THREE.Mesh(geometry, material)
  mesh.name = 'ground'
  mesh.receiveShadow = true
  mesh.position.set(...groundPosition)
  mesh.userData.physics = { mass: 0 }

  scene.add(mesh)

  const { rigidBody, collider } = getPhysic(world, {
    position: groundPosition,
    size: groundSizes,
    boundary: 0.5,
    restitution
  })

  const helper = new THREE.BoxHelper(mesh, 0x000000)
  if (helpers) {
    scene.add(helper)
  }

  return { mesh, rigidBody, helper, collider }
}

/**
 * Populate a given area with a given amount of instances
 * @param config - Instance configuration object
 * @param [config.amount] - Number of instances to create
 * @param [config.size] - Base size [x, y, z] of each instance
 * @param [config.sizeVariation] - Random size variation [x, y, z] to add to base size
 * @param [config.position] - Base position [x, y, z] of instances
 * @param [config.positionVariation] - Random position variation [x, y, z] range
 * @param [config.rotation] - Base rotation [x, y, z] in degrees
 * @param [config.rotationVariation] - Random rotation variation [x, y, z] range in degrees
 * @param [config.area] - Area divisor for legacy positioning
 * @param [config.ratio] - Width to height ratio multiplier (legacy)
 * @param [config.spacing] - Spacing between instances along X axis (legacy)
 * @param [config.opacity] - Opacity value (stored but not used in generation)
 * @returns Array of instance configurations
 */
export const getInstanceConfig = ({
  size = [1, 1, 1],
  sizeVariation = [0, 0, 0],
  position = [0, 0, 0],
  positionVariation = [0, 0, 0],
  rotation = [0, 0, 0],
  rotationVariation = [0, 0, 0],
  amount = 1,
  ratio = 1,
  spacing = 0,
  textures
}: InstanceConfig): GeneratedInstanceConfig => {
  const generatedConfig = times(amount, (index) => {
    const newSize: CoordinateTuple = [
      size[0] + (Math.random() - 0.5) * sizeVariation[0],
      size[1] + (Math.random() - 0.5) * sizeVariation[1],
      size[2] + (Math.random() - 0.5) * sizeVariation[2]
    ]

    const baseX = index * spacing - (amount * spacing) / 2
    const xVariable = positionVariation[0] > 0 ? (Math.random() - 0.5) * positionVariation[0] : 0
    const newPosition: CoordinateTuple = [
      baseX + xVariable,
      position[1] + (Math.random() - 0.5) * positionVariation[1],
      position[2] + (Math.random() - 0.5) * positionVariation[2]
    ]

    // Calculate rotation with variation
    const newRotation: CoordinateTuple = [
      rotation[0] + (Math.random() - 0.5) * rotationVariation[0],
      rotation[1] + (Math.random() - 0.5) * rotationVariation[1],
      rotation[2] + (Math.random() - 0.5) * rotationVariation[2]
    ]

    const scale: CoordinateTuple =
      ratio !== 1 ? [newSize[0] * ratio, newSize[1], newSize[2]] : newSize

    return {
      position: newPosition,
      rotation: newRotation,
      scale,
      textures
    }
  })

  return generatedConfig
}

/**
 * Works out where a camera should sit behind a model, from an offset in the model's own frame.
 * @param model The model to follow.
 * @param config The offset, in the model's frame.
 * @returns The camera position in world space.
 */
export const getOffset = (
  model: Model,
  config: { offset: { x: number; y: number; z: number } }
) => {
  const { x, y, z } = config.offset
  const offset = new THREE.Vector3(x, y, z)
  offset.applyQuaternion(model.quaternion)
  offset.add(model.position)
  return offset
}

type RigidBodyDescOptions = {
  type: NonNullable<PhysicOptions['type']>
  position: NonNullable<PhysicOptions['position']>
  weight: number
  dominance: number
  damping: number
  angular: number
  enabledRotations: [boolean, boolean, boolean]
  ccd: boolean
}

const buildRigidBodyDesc = ({
  type,
  position,
  weight,
  dominance,
  damping,
  angular,
  enabledRotations,
  ccd
}: RigidBodyDescOptions): RAPIER.RigidBodyDesc =>
  RAPIER.RigidBodyDesc[type]()
    .setTranslation(...position)
    .setGravityScale(weight)
    .setDominanceGroup(dominance)
    .setLinearDamping(damping)
    .setAngularDamping(angular)
    .enabledRotations(...enabledRotations)
    .setCcdEnabled(ccd)

const buildColliderShape = (
  shape: PhysicOptions['shape'],
  size: PhysicOptions['size'],
  boundary: number
): RAPIER.ColliderDesc => {
  const sizeValue = size ?? [1, 1, 1]
  if (shape === 'cuboid') {
    const halfExtents = Array.isArray(sizeValue)
      ? (sizeValue.map((x) => x * boundary) as CoordinateTuple)
      : ([sizeValue * boundary, sizeValue * boundary, sizeValue * boundary] as CoordinateTuple)
    return RAPIER.ColliderDesc.cuboid(...halfExtents)
  }
  if (shape === 'cylinder') {
    const [diameter, height] = Array.isArray(sizeValue)
      ? sizeValue
      : [sizeValue, sizeValue, sizeValue]
    return RAPIER.ColliderDesc.cylinder((height * boundary) / 2, (diameter * boundary) / 2)
  }
  // A capsule reads its size the same way a cylinder does, so one can be swapped for the
  // other; the height is the straight section only, with the two hemispherical caps on top.
  if (shape === 'capsule') {
    const [diameter, height] = Array.isArray(sizeValue)
      ? sizeValue
      : [sizeValue, sizeValue, sizeValue]
    return RAPIER.ColliderDesc.capsule((height * boundary) / 2, (diameter * boundary) / 2)
  }
  return RAPIER.ColliderDesc.ball((Array.isArray(sizeValue) ? sizeValue[0] : sizeValue) as number)
}

const buildCharacterController = (world: RAPIER.World): RAPIER.KinematicCharacterController => {
  const controller = world.createCharacterController(0.01)
  controller.setUp({ x: 0, y: 1, z: 0 })
  controller.enableSnapToGround(0)
  controller.setMaxSlopeClimbAngle(45)
  controller.setMinSlopeSlideAngle(30)
  return controller
}

const applyRotationToRigidBody = (
  rigidBody: RAPIER.RigidBody,
  rotation: PhysicOptions['rotation']
) => {
  if (rotation && rotation instanceof Array) {
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(...rotation))
    rigidBody.setRotation(q, true)
  }
}

type ColliderOptions = {
  shape: PhysicOptions['shape']
  size: PhysicOptions['size']
  boundary: number
  contactSkin: number
  restitution: number
  friction: number
  mass: number
  density: number
}

const buildCollider = (
  world: RAPIER.World,
  rigidBody: RAPIER.RigidBody,
  options: ColliderOptions
): RAPIER.Collider => {
  const { shape, size, boundary, contactSkin, restitution, friction, mass, density } = options
  const colliderDesc = buildColliderShape(shape, size, boundary)
    .setContactSkin(contactSkin)
    .setRestitution(restitution)
    .setFriction(friction)
    .setMass(mass)
    .setDensity(density)
  return world.createCollider(colliderDesc, rigidBody)
}

/**
 * Add physic to the model for a given world, using default values
 * @param world
 * @param position
 * @param size
 * @param {object} options - The options for the physics body and collider.
 * @param {number} [options.boundary=0] - The boundary size of the collider.
 * @param {number} [options.restitution=0] - The restitution (bounciness) of the collider.
 * @param {number} [options.friction=0] - The friction of the collider against other objects.
 * @param {Rotation} [options.rotation] - The initial rotation of the object.
 * @param {number} [options.weight=1] - The gravity scale applied to the object to simulate the weight.
 * @param {number} [options.mass=1] - The mass in the sense of resistance from movement.
 * @param {number} [options.density=1] - The density of the object.
 * @param {number} [options.dominance=1] - The influence level from other bodies.
 * @param {'fixed' | 'dynamic'} [options.type='fixed'] - The type of the rigid body.
 * @param {'cuboid' | 'ball' | 'cylinder' | 'capsule'} [options.shape='cuboid'] - The shape of the collider.
 * @param {boolean} [options.ccd=false] - Continuous collision detection: sweeps the body's whole path each step, for anything fast enough to pass through what it should hit.
 * @returns {object} The created rigid body and collider.
 * @returns
 */

type ResolvedPhysicOptions = Required<
  Pick<
    PhysicOptions,
    | 'rotation'
    | 'position'
    | 'size'
    | 'boundary'
    | 'contactSkin'
    | 'restitution'
    | 'friction'
    | 'damping'
    | 'angular'
    | 'mass'
    | 'density'
    | 'weight'
    | 'dominance'
    | 'shape'
    | 'type'
    | 'enabledRotations'
    | 'ccd'
  >
>

const resolvePhysicBodyOptions = (options: PhysicOptions) => ({
  rotation: options.rotation ?? ([0, 0, 0] as NonNullable<PhysicOptions['rotation']>),
  position: options.position ?? ([0, 0, 0] as NonNullable<PhysicOptions['position']>),
  weight: options.weight ?? 1,
  dominance: options.dominance ?? 0,
  damping: options.damping ?? 0,
  angular: options.angular ?? 1,
  enabledRotations: options.enabledRotations ?? ([true, true, true] as [boolean, boolean, boolean]),
  type: options.type ?? ('fixed' as NonNullable<PhysicOptions['type']>),
  ccd: options.ccd ?? false
})

const resolvePhysicColliderOptions = (options: PhysicOptions) => ({
  size: options.size ?? ([1, 1, 1] as NonNullable<PhysicOptions['size']>),
  boundary: options.boundary ?? 0.5,
  contactSkin: options.contactSkin ?? 0,
  restitution: options.restitution ?? 1,
  friction: options.friction ?? 0,
  mass: options.mass ?? 1,
  density: options.density ?? 1,
  shape: options.shape ?? ('cuboid' as NonNullable<PhysicOptions['shape']>)
})

const resolvePhysicOptions = (options: PhysicOptions): ResolvedPhysicOptions => ({
  ...resolvePhysicBodyOptions(options),
  ...resolvePhysicColliderOptions(options)
})

/**
 * Creates the rigid body and collider for a model, and a character controller for kinematic bodies.
 * @param world The physics world to add them to.
 * @param options The body type, shape, size, position, rotation and material settings.
 * @returns The rigid body, its collider, and the character controller when one was made.
 */
export const getPhysic = (world: RAPIER.World, options: PhysicOptions) => {
  const {
    rotation,
    position,
    size,
    boundary,
    contactSkin,
    restitution,
    friction,
    damping,
    angular,
    mass,
    density,
    weight,
    dominance,
    shape,
    type,
    enabledRotations,
    ccd
  } = resolvePhysicOptions(options)
  const rigidBodyDesc = buildRigidBodyDesc({
    type,
    position,
    weight,
    dominance,
    damping,
    angular,
    enabledRotations: enabledRotations as [boolean, boolean, boolean],
    ccd
  })
  const rigidBody = world.createRigidBody(rigidBodyDesc)
  applyRotationToRigidBody(rigidBody, rotation)
  const collider = buildCollider(world, rigidBody, {
    shape,
    size,
    boundary,
    contactSkin,
    restitution,
    friction,
    mass,
    density
  })
  const characterController =
    type === 'kinematicPositionBased' ? buildCharacterController(world) : undefined

  return { rigidBody, collider, characterController }
}

/**
 * A phone's device pixel ratio (often 3-4) renders 9-16x the fragments of a
 * ratio of 1, and fill rate is usually what a phone runs out of first.
 * Capping it at 2 is close to invisible on a small screen and is the
 * cheapest, largest win available for mobile frame time.
 * @param devicePixelRatio - The display's actual device pixel ratio
 * @param maxPixelRatio - The highest ratio the renderer is allowed to use
 * @returns The pixel ratio to render at
 */
export const getPixelRatio = (devicePixelRatio: number, maxPixelRatio = 2): number =>
  Math.min(devicePixelRatio, maxPixelRatio)

/**
 * Creates an antialiased renderer with transparency and shadows enabled.
 * @param canvas The canvas to render into.
 * @returns The renderer.
 */
export const getRenderer = (canvas: HTMLCanvasElement): THREE.WebGLRenderer => {
  // Alpha is always on: it costs nothing for a scene that clears to an opaque colour,
  // and it is what lets `scene.transparent` show a layer behind the canvas later.
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true })
  renderer.setPixelRatio(getPixelRatio(window.devicePixelRatio))
  renderer.setSize(canvas.clientWidth, canvas.clientHeight)
  renderer.setClearColor(0xaaaaff)
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  return renderer
}

/**
 * Adds a sky sphere around the scene, coloured or textured on its inside.
 * @param scene The scene to add the sky to.
 * @returns The sky mesh.
 */
export const getSky = (
  scene: THREE.Scene,
  {
    color = SCENE_DEFAULTS.sky.color,
    size = SCENE_DEFAULTS.sky.size,
    texture
  }: {
    color?: number
    texture?: string
    size?: number
  }
) => {
  const skyGeometry = new THREE.SphereGeometry(size, 32, 32)
  const skyMaterial = new THREE.MeshBasicMaterial({
    side: THREE.BackSide,
    color,
    ...(texture
      ? { map: textureLoader.load(new URL(texture!, import.meta.url) as unknown as string) }
      : {})
  })
  const model = new THREE.Mesh(skyGeometry, skyMaterial)
  model.name = 'sky'
  scene.add(model)
  return { model }
}

/**
 * Get default textures
 * @param img URL of the image.
 * @param repeat How many times it tiles across and down.
 * @param offset Where the tiling starts.
 * @returns The texture, in sRGB and set to repeat.
 */
export const getTextures = (
  img: string,
  repeat: [number, number] = [1, 1],
  offset: [number, number] = [0, 0]
) => {
  const texture = textureLoader.load(img)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.offset.set(offset[0], offset[1])
  texture.repeat.set(repeat[0], repeat[1])
  return texture
}
